/**
 * LeagueViewPage.js — Read-only league overview with edit navigation
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getLeague, getPlayoffs } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { getSportConfig, getOfficialTitle } from '../utils/sportConfig';
import './LeagueViewPage.css';

function LeagueViewPage({ leagueId, onBack, onEditSchedule, onEditScores, onEditPlayoffs, onGameManagement }) {
  const { user } = useAuth();
  const userPlan = user?.plan || 'free';

  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('schedule');

  // Lazy-loaded data
  const [playoffs, setPlayoffs] = useState(null);
  const [playoffsLoaded, setPlayoffsLoaded] = useState(false);
  const [venuesLoaded, setVenuesLoaded] = useState(false);

  // Clipboard toast
  const [toast, setToast] = useState('');

  useEffect(() => {
    loadLeague();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  const loadLeague = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getLeague(leagueId);
      setLeague(data.league);
    } catch (err) {
      setError(err.message || 'Failed to load league');
    } finally {
      setLoading(false);
    }
  };

  const handleTabSwitch = useCallback(async (tab) => {
    setActiveTab(tab);

    if (tab === 'playoffs' && !playoffsLoaded) {
      try {
        const data = await getPlayoffs(leagueId);
        setPlayoffs(data.playoffs);
      } catch (err) {
        console.error('Failed to load playoffs:', err);
      } finally {
        setPlayoffsLoaded(true);
      }
    }

    if (tab === 'venues' && !venuesLoaded) {
      setVenuesLoaded(true);
    }
  }, [leagueId, playoffsLoaded, venuesLoaded]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setToast('Copied!');
    setTimeout(() => setToast(''), 2000);
  };

  // ── Loading / Error ──

  if (loading) {
    return (
      <div className="league-view-page">
        <div className="league-view-loading">Loading league data...</div>
      </div>
    );
  }

  if (error || !league) {
    return (
      <div className="league-view-page">
        <div className="league-view-error">
          <h2>Failed to load league</h2>
          <p>{error || 'League not found'}</p>
          <button onClick={onBack} className="league-view-back-btn">Back to Dashboard</button>
        </div>
      </div>
    );
  }

  // ── Derived data ──

  const isRotatingPartners = league.formatType === 'rotating_partners';
  const sportConfig = getSportConfig(league.sport);
  const teams = league.teams || [];
  const leaguePlayers = league.leaguePlayers || [];
  const games = league.games || [];
  const regularGames = games.filter(g => !g.isPlayoff);
  const playedGames = regularGames.filter(g => g.played);

  // Team lookup map
  const teamMap = {};
  teams.forEach(t => { teamMap[t.id] = t; });

  // Games grouped by week
  const gamesByWeek = {};
  regularGames.forEach(game => {
    const w = game.week;
    if (!gamesByWeek[w]) gamesByWeek[w] = { week: w, date: game.date, games: [] };
    gamesByWeek[w].games.push(game);
  });
  const weeks = Object.values(gamesByWeek).sort((a, b) => a.week - b.week);

  // Standings sorted
  const standings = [...teams].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    const aDiff = (a.goalsFor || 0) - (a.goalsAgainst || 0);
    const bDiff = (b.goalsFor || 0) - (b.goalsAgainst || 0);
    if (bDiff !== aDiff) return bDiff - aDiff;
    if ((b.goalsFor || 0) !== (a.goalsFor || 0)) return (b.goalsFor || 0) - (a.goalsFor || 0);
    return b.wins - a.wins;
  });

  // Groups
  const group1 = teams.filter(t => t.groupNumber !== 2);
  const group2 = teams.filter(t => t.groupNumber === 2);

  // Schedule config
  const originalInput = league.scheduleData?.originalInput;
  const timeSlots = originalInput?.timeSlots || [];
  const gameDates = originalInput?.gameDates || originalInput?.allWeeks || [];

  // Public URL
  const publicUrl = league.slug
    ? `${window.location.origin}/public/${league.slug}`
    : null;

  // ── Tab config ──

  const tabs = [
    { key: 'schedule', label: 'Schedule' },
    { key: 'standings', label: isRotatingPartners ? 'Player Standings' : 'Standings' },
    { key: 'teams', label: isRotatingPartners ? 'Players' : 'Teams' },
    { key: 'config', label: 'Config' },
  ];
  if (league.playoffSettings?.enabled) {
    tabs.push({ key: 'playoffs', label: 'Playoffs' });
  }
  if (userPlan !== 'free') {
    tabs.push({ key: 'venues', label: 'Venues' });
  }
  if (userPlan === 'pro') {
    tabs.push({ key: 'referees', label: `${getOfficialTitle(league.sport)}s` });
  }
  tabs.push({ key: 'settings', label: 'Settings' });

  // ── Render helpers ──

  const renderGameStatus = (game) => {
    if (game.status === 'forfeit') {
      const forfeitName = game.forfeitTeam?.name || 'Unknown';
      return <span className="league-view-status-forfeit">FORFEIT &mdash; {forfeitName}</span>;
    }
    if (game.status === 'cancelled') {
      return <span className="league-view-status-cancelled">CANCELLED</span>;
    }
    if (game.status === 'postponed') {
      const reschedDate = game.rescheduledDate ? formatDate(game.rescheduledDate) : '';
      const reschedTime = game.rescheduledTime || '';
      return (
        <span className="league-view-status-postponed">
          POSTPONED{reschedDate ? ` \u2014 Rescheduled to ${reschedDate}` : ''}{reschedTime ? ` at ${reschedTime}` : ''}
        </span>
      );
    }

    // Rotating-partner display
    if (isRotatingPartners && game.partnerMatchData) {
      const pmd = game.partnerMatchData;
      const t1Name = pmd.team1 ? pmd.team1.map(p => p.name).join(' + ') : 'TBD';
      const t2Name = pmd.team2 ? pmd.team2.map(p => p.name).join(' + ') : 'TBD';
      if (game.played && game.team1Score != null && game.team2Score != null) {
        return (
          <span className="league-view-game-matchup">
            <span className="league-view-team-name-text">{t1Name}</span>
            <span className="league-view-game-score">{game.team1Score} - {game.team2Score}</span>
            <span className="league-view-team-name-text">{t2Name}</span>
          </span>
        );
      }
      return (
        <span className="league-view-game-matchup">
          <span className="league-view-team-name-text">{t1Name}</span>
          <span className="league-view-vs">vs</span>
          <span className="league-view-team-name-text">{t2Name}</span>
        </span>
      );
    }

    // Standard team display
    const t1 = teamMap[game.team1Id];
    const t2 = teamMap[game.team2Id];
    const t1Name = t1?.name || 'TBD';
    const t2Name = t2?.name || 'TBD';

    if (game.played && game.team1Score != null && game.team2Score != null) {
      return (
        <span className="league-view-game-matchup">
          <span className="league-view-team-name-text">{t1Name}</span>
          <span className="league-view-game-score">{game.team1Score} - {game.team2Score}</span>
          <span className="league-view-team-name-text">{t2Name}</span>
        </span>
      );
    }
    return (
      <span className="league-view-game-matchup">
        <span className="league-view-team-name-text">{t1Name}</span>
        <span className="league-view-vs">vs</span>
        <span className="league-view-team-name-text">{t2Name}</span>
      </span>
    );
  };

  const renderGameBadge = (game) => {
    if (game.status === 'forfeit' || game.status === 'cancelled' || game.status === 'postponed') return null;
    if (game.played) {
      return <span className="league-view-game-status-badge league-view-status-completed">Completed</span>;
    }
    return <span className="league-view-game-status-badge league-view-status-upcoming">Upcoming</span>;
  };

  // ── Section renderers ──

  const renderSchedule = () => (
    <div className="league-view-section">
      <div className="league-view-section-header">
        <h2 className="league-view-section-title">Schedule</h2>
        <button className="league-view-edit-btn" onClick={() => onGameManagement(leagueId)}>
          Manage Games
        </button>
      </div>
      {weeks.length === 0 ? (
        <div className="league-view-empty">No games scheduled yet.</div>
      ) : (
        weeks.map(weekData => (
          <div key={weekData.week} className="league-view-week-card">
            <div className="league-view-week-header">
              <span className="league-view-week-label">Week {weekData.week}</span>
              <span className="league-view-week-date">{formatDate(weekData.date)}</span>
            </div>
            {weekData.games.map(game => (
              <div key={game.id} className="league-view-game-row">
                <div className="league-view-game-time-col">
                  <div className="league-view-game-time">{game.time}</div>
                  {game.venue && (
                    <div className="league-view-game-detail">{game.venue.name}</div>
                  )}
                  {game.referee && (
                    <div className="league-view-game-detail">{game.referee.name}</div>
                  )}
                </div>
                <div>{renderGameStatus(game)}</div>
                <div>{renderGameBadge(game)}</div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );

  const renderStandings = () => {
    if (isRotatingPartners) {
      const playerStandings = [...leaguePlayers].sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.wins !== a.wins) return b.wins - a.wins;
        const aDiff = (a.pointsFor || 0) - (a.pointsAgainst || 0);
        const bDiff = (b.pointsFor || 0) - (b.pointsAgainst || 0);
        if (bDiff !== aDiff) return bDiff - aDiff;
        return a.name.localeCompare(b.name);
      });
      return (
        <div className="league-view-section">
          <div className="league-view-section-header">
            <h2 className="league-view-section-title">Player Standings</h2>
          </div>
          {playerStandings.length === 0 ? (
            <div className="league-view-empty">No players yet.</div>
          ) : (
            <table className="league-view-standings-table">
              <thead>
                <tr>
                  <th className="league-view-rank-col">#</th>
                  <th className="league-view-team-col">Player</th>
                  <th className="league-view-stat-col">W</th>
                  <th className="league-view-stat-col">L</th>
                  <th className="league-view-stat-col">PTS</th>
                  <th className="league-view-stat-col">GP</th>
                  <th className="league-view-stat-col">PF</th>
                  <th className="league-view-stat-col">PA</th>
                  <th className="league-view-stat-col">Rating</th>
                </tr>
              </thead>
              <tbody>
                {playerStandings.map((player, i) => (
                  <tr key={player.id}>
                    <td className="league-view-rank-col">{i + 1}</td>
                    <td className="league-view-team-col">{player.name}</td>
                    <td className="league-view-stat-col">{player.wins}</td>
                    <td className="league-view-stat-col">{player.losses}</td>
                    <td className="league-view-stat-col">{player.points}</td>
                    <td className="league-view-stat-col">{player.gamesPlayed}</td>
                    <td className="league-view-stat-col">{player.pointsFor || 0}</td>
                    <td className="league-view-stat-col">{player.pointsAgainst || 0}</td>
                    <td className="league-view-stat-col">{player.skillRating != null ? player.skillRating.toFixed(1) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      );
    }

    return (
      <div className="league-view-section">
        <div className="league-view-section-header">
          <h2 className="league-view-section-title">Standings</h2>
        </div>
        {standings.length === 0 ? (
          <div className="league-view-empty">No teams yet.</div>
        ) : (
          <table className="league-view-standings-table">
            <thead>
              <tr>
                <th className="league-view-rank-col">#</th>
                <th className="league-view-team-col">Team</th>
                <th className="league-view-stat-col">W</th>
                <th className="league-view-stat-col">L</th>
                {sportConfig.allowTies && <th className="league-view-stat-col">T</th>}
                <th className="league-view-stat-col">PTS</th>
                <th className="league-view-stat-col">{sportConfig.statLabels.goalsFor}</th>
                <th className="league-view-stat-col">{sportConfig.statLabels.goalsAgainst}</th>
                <th className="league-view-stat-col">{sportConfig.statLabels.goalDifferential}</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((team, i) => (
                <tr key={team.id}>
                  <td className="league-view-rank-col">{i + 1}</td>
                  <td className="league-view-team-col">{team.name}</td>
                  <td className="league-view-stat-col">{team.wins}</td>
                  <td className="league-view-stat-col">{team.losses}</td>
                  {sportConfig.allowTies && <td className="league-view-stat-col">{team.ties}</td>}
                  <td className="league-view-stat-col">{team.points}</td>
                  <td className="league-view-stat-col">{team.goalsFor || 0}</td>
                  <td className="league-view-stat-col">{team.goalsAgainst || 0}</td>
                  <td className="league-view-stat-col">{(team.goalsFor || 0) - (team.goalsAgainst || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  const renderTeams = () => {
    if (isRotatingPartners) {
      const sortedPlayers = [...leaguePlayers].sort((a, b) => b.skillRating - a.skillRating || a.name.localeCompare(b.name));
      return (
        <div className="league-view-section">
          <div className="league-view-section-header">
            <h2 className="league-view-section-title">Players</h2>
          </div>
          {sortedPlayers.length === 0 ? (
            <div className="league-view-empty">No players configured.</div>
          ) : (
            sortedPlayers.map(player => (
              <div key={player.id} className="league-view-team-row">
                <div className="league-view-team-info">
                  <span className="league-view-team-name-text">{player.name}</span>
                  {player.email && (
                    <span className="league-view-team-captain">{player.email}</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--color-brand)', fontWeight: 500 }}>Rating: {player.skillRating?.toFixed(1)}</span>
                  <span className="league-view-team-record">{player.wins}-{player.losses}</span>
                </div>
              </div>
            ))
          )}
        </div>
      );
    }
    return (
    <div className="league-view-section">
      <div className="league-view-section-header">
        <h2 className="league-view-section-title">Teams</h2>
        <button className="league-view-edit-btn" onClick={() => onEditSchedule(leagueId)}>
          Edit Teams
        </button>
      </div>
      {group2.length > 0 && <div className="league-view-group-header">Group 1</div>}
      {group1.map(team => {
        const capName = team.captain?.name || team.captainName;
        const capEmail = team.captain?.email || team.captainEmail;
        return (
          <div key={team.id} className="league-view-team-row">
            <div className="league-view-team-info">
              <span className="league-view-team-name-text">{team.name}</span>
              {(capName || capEmail) && (
                <span className="league-view-team-captain">
                  {capName}{capName && capEmail ? ' \u2014 ' : ''}{capEmail}
                </span>
              )}
            </div>
            <span className="league-view-team-record">{team.wins}-{team.losses}{sportConfig.allowTies ? `-${team.ties}` : ''}</span>
          </div>
        );
      })}
      {group2.length > 0 && (
        <>
          <div className="league-view-group-header">Group 2</div>
          {group2.map(team => {
            const capName = team.captain?.name || team.captainName;
            const capEmail = team.captain?.email || team.captainEmail;
            return (
              <div key={team.id} className="league-view-team-row">
                <div className="league-view-team-info">
                  <span className="league-view-team-name-text">{team.name}</span>
                  {(capName || capEmail) && (
                    <span className="league-view-team-captain">
                      {capName}{capName && capEmail ? ' \u2014 ' : ''}{capEmail}
                    </span>
                  )}
                </div>
                <span className="league-view-team-record">{team.wins}-{team.losses}{sportConfig.allowTies ? `-${team.ties}` : ''}</span>
              </div>
            );
          })}
        </>
      )}
      {teams.length === 0 && <div className="league-view-empty">No teams configured.</div>}
    </div>
    );
  };

  const renderConfig = () => (
    <div className="league-view-section">
      <div className="league-view-section-header">
        <h2 className="league-view-section-title">Configuration</h2>
        <button className="league-view-edit-btn" onClick={() => onEditSchedule(leagueId)}>
          Edit Configuration
        </button>
      </div>
      <div className="league-view-config-grid">
        <div className="league-view-config-card">
          <div className="league-view-config-label">Sport</div>
          <div className="league-view-config-value">{sportConfig.label}</div>
        </div>
        <div className="league-view-config-card">
          <div className="league-view-config-label">Season</div>
          <div className="league-view-config-value">{league.seasonYear || 'N/A'}</div>
        </div>
        <div className="league-view-config-card">
          <div className="league-view-config-label">Playoffs</div>
          <div className="league-view-config-value">
            {league.playoffSettings?.enabled
              ? `Enabled (Top ${league.playoffSettings.teamsQualifying})`
              : 'Disabled'}
          </div>
        </div>
        <div className="league-view-config-card">
          <div className="league-view-config-label">Score Notifications</div>
          <div className="league-view-config-value">{league.notifyOnScores ? 'On' : 'Off'}</div>
        </div>
      </div>

      {timeSlots.length > 0 && (
        <>
          <div className="league-view-group-header">Time Slots</div>
          <ul className="league-view-config-list">
            {timeSlots.map((slot, i) => <li key={i}>{slot}</li>)}
          </ul>
        </>
      )}

      {gameDates.length > 0 && (
        <>
          <div className="league-view-group-header">Game Dates ({gameDates.length})</div>
          <ul className="league-view-config-list">
            {gameDates.map((d, i) => {
              const dateStr = typeof d === 'object' ? d.date : d;
              return <li key={i}>{formatDate(dateStr)}</li>;
            })}
          </ul>
        </>
      )}
    </div>
  );

  const renderPlayoffs = () => {
    if (!playoffsLoaded) {
      return (
        <div className="league-view-section">
          <div className="league-view-loading">Loading playoff data...</div>
        </div>
      );
    }

    if (!playoffs) {
      return (
        <div className="league-view-section">
          <div className="league-view-section-header">
            <h2 className="league-view-section-title">Playoffs</h2>
            {userPlan === 'pro' && (
              <button className="league-view-edit-btn" onClick={() => onEditPlayoffs(leagueId)}>
                Manage Playoffs
              </button>
            )}
          </div>
          <div className="league-view-empty">Playoff bracket has not been generated yet.</div>
        </div>
      );
    }

    // Find champion
    const finalRound = playoffs.rounds[playoffs.rounds.length - 1];
    const finalGame = finalRound?.games?.[0];
    let champion = null;
    if (finalGame?.played) {
      if (finalGame.overrideWinnerId) {
        champion = finalGame.team1?.id === finalGame.overrideWinnerId ? finalGame.team1 : finalGame.team2;
      } else if (finalGame.team1Score != null && finalGame.team2Score != null) {
        champion = finalGame.team1Score > finalGame.team2Score ? finalGame.team1 : finalGame.team2;
      }
    }

    return (
      <div className="league-view-section">
        <div className="league-view-section-header">
          <h2 className="league-view-section-title">Playoffs</h2>
          {userPlan === 'pro' && (
            <button className="league-view-edit-btn" onClick={() => onEditPlayoffs(leagueId)}>
              Manage Playoffs
            </button>
          )}
        </div>

        {champion && (
          <div className="league-view-champion-banner">
            Champion: {champion.name}
          </div>
        )}

        {playoffs.rounds.map(round => (
          <div key={round.round} className="league-view-round-card">
            <div className="league-view-round-name">{round.name}</div>
            {round.games.map(game => {
              const t1Name = game.team1 ? game.team1.name : 'TBD';
              const t2Name = game.team2 ? game.team2.name : 'TBD';
              const t1Seed = game.team1?.seed;
              const t2Seed = game.team2?.seed;

              return (
                <div key={game.id} className="league-view-playoff-game">
                  <div className="league-view-game-matchup">
                    {t1Seed && <span className="league-view-seed">({t1Seed}) </span>}
                    <span className={game.team1 ? 'league-view-team-name-text' : 'league-view-tbd'}>{t1Name}</span>
                    {game.played && game.team1Score != null ? (
                      <span className="league-view-game-score">{game.team1Score} - {game.team2Score}</span>
                    ) : (
                      <span className="league-view-vs">vs</span>
                    )}
                    {t2Seed && <span className="league-view-seed">({t2Seed}) </span>}
                    <span className={game.team2 ? 'league-view-team-name-text' : 'league-view-tbd'}>{t2Name}</span>
                    {game.overrideWinnerId && (
                      <span className="league-view-override-badge" title={game.overrideReason || 'Admin override'}>
                        Override
                      </span>
                    )}
                  </div>
                  {game.time && <span className="league-view-game-detail">{game.time}</span>}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  const renderVenues = () => {
    // Derive venues from game records (venues are user-scoped, not league-scoped)
    const venueMap = {};
    regularGames.forEach(g => {
      if (g.venue) {
        if (!venueMap[g.venue.id]) {
          venueMap[g.venue.id] = { ...g.venue, gameCount: 0 };
        }
        venueMap[g.venue.id].gameCount++;
      }
    });
    const derivedVenues = Object.values(venueMap).sort((a, b) => a.name.localeCompare(b.name));

    return (
      <div className="league-view-section">
        <div className="league-view-section-header">
          <h2 className="league-view-section-title">Venues Used</h2>
          <button className="league-view-edit-btn" onClick={() => onGameManagement(leagueId)}>
            Manage Games
          </button>
        </div>
        {derivedVenues.length === 0 ? (
          <div className="league-view-empty">No venues assigned to games in this league.</div>
        ) : (
          derivedVenues.map(venue => (
            <div key={venue.id} className="league-view-list-item">
              <div>
                <div className="league-view-list-name">{venue.name}</div>
                {venue.address && <div className="league-view-list-detail">{venue.address}</div>}
              </div>
              <span className="league-view-list-badge">
                {venue.gameCount} game{venue.gameCount !== 1 ? 's' : ''}
              </span>
            </div>
          ))
        )}
      </div>
    );
  };

  const renderReferees = () => {
    const officialTitle = getOfficialTitle(league.sport);
    // Derive referees from game records (referees are user-scoped, not league-scoped)
    const refMap = {};
    regularGames.forEach(g => {
      if (g.referee) {
        if (!refMap[g.referee.id]) {
          refMap[g.referee.id] = { ...g.referee, gameCount: 0 };
        }
        refMap[g.referee.id].gameCount++;
      }
    });
    const derivedReferees = Object.values(refMap).sort((a, b) => a.name.localeCompare(b.name));

    return (
      <div className="league-view-section">
        <div className="league-view-section-header">
          <h2 className="league-view-section-title">{officialTitle}s Used</h2>
          <button className="league-view-edit-btn" onClick={() => onGameManagement(leagueId)}>
            Manage Games
          </button>
        </div>
        {derivedReferees.length === 0 ? (
          <div className="league-view-empty">No {officialTitle.toLowerCase()}s assigned to games in this league.</div>
        ) : (
          derivedReferees.map(ref => (
            <div key={ref.id} className="league-view-list-item">
              <div>
                <div className="league-view-list-name">{ref.name}</div>
                {ref.email && <div className="league-view-list-detail">{ref.email}</div>}
                {ref.phone && <div className="league-view-list-detail">{ref.phone}</div>}
              </div>
              <span className="league-view-list-badge">
                {ref.gameCount} game{ref.gameCount !== 1 ? 's' : ''}
              </span>
            </div>
          ))
        )}
      </div>
    );
  };

  const renderSettings = () => (
    <div className="league-view-section">
      <div className="league-view-section-header">
        <h2 className="league-view-section-title">Public Page Settings</h2>
        {userPlan !== 'free' && (
          <button className="league-view-edit-btn" onClick={onBack}>
            Customize on Dashboard
          </button>
        )}
      </div>
      <div className="league-view-setting-row">
        <span className="league-view-setting-label">Public Page</span>
        <span className={league.isPublic ? 'league-view-on' : 'league-view-off'}>
          {league.isPublic ? 'On' : 'Off'}
        </span>
      </div>
      <div className="league-view-setting-row">
        <span className="league-view-setting-label">Show Standings</span>
        <span className={league.showStandings ? 'league-view-on' : 'league-view-off'}>
          {league.showStandings ? 'On' : 'Off'}
        </span>
      </div>
      <div className="league-view-setting-row">
        <span className="league-view-setting-label">Captain Score Submission</span>
        <span className={league.captainScoreEntry ? 'league-view-on' : 'league-view-off'}>
          {league.captainScoreEntry ? 'On' : 'Off'}
        </span>
      </div>

      {league.isPublic && publicUrl && (
        <div style={{ marginTop: '1rem' }}>
          <div className="league-view-config-label">Public URL</div>
          <div className="league-view-url-box">
            <span style={{ flex: 1 }}>{publicUrl}</span>
            <button className="league-view-copy-btn" onClick={() => copyToClipboard(publicUrl)} title="Copy URL">
              {'\uD83D\uDCCB'}
            </button>
          </div>
        </div>
      )}

      {league.primaryColor && (
        <div style={{ marginTop: '1rem' }}>
          <div className="league-view-config-label">Brand Color</div>
          <div>
            <span className="league-view-color-swatch" style={{ backgroundColor: league.primaryColor }} />
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>{league.primaryColor}</span>
          </div>
        </div>
      )}

      {league.logoUrl && (
        <div style={{ marginTop: '1rem' }}>
          <div className="league-view-config-label">Logo</div>
          <img
            src={league.logoUrl}
            alt="League logo"
            className="league-view-logo-preview"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}

      {league.description && (
        <div style={{ marginTop: '1rem' }}>
          <div className="league-view-config-label">Description</div>
          <div style={{ color: 'var(--color-text)', fontSize: '0.9rem' }}>{league.description}</div>
        </div>
      )}
    </div>
  );

  // ── Main render ──

  return (
    <div className="league-view-page">
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '1rem', right: '1rem', zIndex: 9999,
          background: 'var(--color-toast-success-bg)', color: 'var(--color-toast-success-text)',
          padding: '0.5rem 1rem', borderRadius: '8px', fontWeight: 600, fontSize: '0.85rem'
        }}>
          {toast}
        </div>
      )}

      {/* Header */}
      <div className="league-view-header">
        <div className="league-view-top-row">
          <button className="league-view-back-btn" onClick={onBack}>&larr; Dashboard</button>
          <h1 className="league-view-title">{league.name}</h1>
        </div>
        <div className="league-view-meta">
          <span className={`league-view-badge ${league.status === 'draft' ? 'league-view-badge-draft' : 'league-view-badge-published'}`}>
            {league.status}
          </span>
          {league.sport && league.sport !== 'general' && (
            <span className="league-view-badge league-view-badge-sport">{sportConfig.label}</span>
          )}
          {league.seasonYear && (
            <span className="league-view-season">{league.seasonYear}</span>
          )}
        </div>
        <div className="league-view-stats-row">
          <div className="league-view-stat-card">
            <div className="league-view-stat-value">{teams.length}</div>
            <div className="league-view-stat-label">Teams</div>
          </div>
          <div className="league-view-stat-card">
            <div className="league-view-stat-value">{regularGames.length}</div>
            <div className="league-view-stat-label">Games</div>
          </div>
          <div className="league-view-stat-card">
            <div className="league-view-stat-value">{playedGames.length}</div>
            <div className="league-view-stat-label">Played</div>
          </div>
          <div className="league-view-stat-card">
            <div className="league-view-stat-value">{gameDates.length}</div>
            <div className="league-view-stat-label">Weeks</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="league-view-tabs">
        {tabs.map(tab => (
          <button
            key={tab.key}
            className={`league-view-tab${activeTab === tab.key ? ' active' : ''}`}
            onClick={() => handleTabSwitch(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === 'schedule' && renderSchedule()}
      {activeTab === 'standings' && renderStandings()}
      {activeTab === 'teams' && renderTeams()}
      {activeTab === 'config' && renderConfig()}
      {activeTab === 'playoffs' && renderPlayoffs()}
      {activeTab === 'venues' && renderVenues()}
      {activeTab === 'referees' && renderReferees()}
      {activeTab === 'settings' && renderSettings()}
    </div>
  );
}

export default LeagueViewPage;
